import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { ELEVENLABS_CONFIG } from "../../config/elevenlabs-api";
import {
  Tabs,
  Card,
  Spin,
  Badge,
  Typography,
  Space,
  Row,
  Col,
  Table,
  Button,
  Descriptions,
  Timeline,
  Empty,
} from "antd";
import { CloseOutlined, PlayCircleOutlined } from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const ConversationDetailTabs = ({ conversation, onClose }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [clientData, setClientData] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);

  const [audioError, setAudioError] = useState("");

  // Utility to format duration in seconds to Xm Ys or just Ys
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "N/A";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const API_BASE_URL = "https://api.honda.showapps.in/api";
  // const API_BASE_URL = "http://localhost:5001/api";
  useEffect(() => {
    if (activeTab === "client-data") {
      fetchClientData();
    } else if (activeTab === "batch-call") {
      fetchBatchData();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchAudio();
  }, []);

  const fetchAudio = async () => {
    setAudioLoading(true);
    setAudioError("");
    try {
      const conversationId = conversation.conversation_id || conversation.id;
      const response = await axios.get(
        `${ELEVENLABS_CONFIG.BASE_URL}/conversations/${conversationId}/audio`,
        {
          headers: {
            "xi-api-key": ELEVENLABS_CONFIG.API_KEY,
          },
          responseType: "blob",
        }
      );
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
    } catch (error) {
      console.error("Error fetching audio:", error);
      setAudioError("Failed to load audio");
    } finally {
      setAudioLoading(false);
    }
  };

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/clients/${conversation.client_id}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setClientData(response.data);
    } catch (error) {
      console.error("Error fetching client data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchData = async () => {
    setLoading(true);
    console.log(conversation, "conversation.batch_call.batch_call_id <<<<<<<<");
    console.log(
      conversation.metadata.batch_call.batch_call_id,
      "conversation.metadata.batch_call.batch_call_id >>>>>>>>>>>>>>>"
    );
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/batch-calls/${conversation.metadata.batch_call.batch_call_id}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      console.log(response, "response <<<<<<");
      setBatchData(response.data);
    } catch (error) {
      console.error("Error fetching batch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: "overview",
      label: "Overview",
      children: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={4}>Report Summary</Title>

          {audioLoading ? (
            <Spin tip="Loading audio..." />
          ) : audioError ? (
            <Text type="danger">{audioError}</Text>
          ) : audioUrl ? (
            <Card title="Conversation Audio" size="small">
              <audio controls src={audioUrl} style={{ width: "100%" }} />
            </Card>
          ) : null}

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Summary" size="small">
                <Paragraph>
                  {conversation.summary || "No summary available"}
                </Paragraph>
                {conversation.analysis?.transcript_summary && (
                  <>
                    <Title level={5} style={{ marginTop: 16 }}>
                      Transcript Summary
                    </Title>
                    <Paragraph type="secondary">
                      {conversation.analysis.transcript_summary}
                    </Paragraph>
                  </>
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Call Status" size="small">
                <Badge
                  status={
                    conversation.status === "completed"
                      ? "success"
                      : "processing"
                  }
                  text={conversation.status}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="User ID" size="small">
                <Text>{conversation.user_id || "N/A"}</Text>
              </Card>
            </Col>
          </Row>
        </Space>
      ),
    },
    {
      key: "transcription",
      label: "Transcription",
      children: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={4}>Report Transcript</Title>

          {conversation.transcript ? (
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <Timeline
                items={conversation.transcript.map((message, index) => ({
                  children: (
                    <Card
                      size="small"
                      style={{
                        backgroundColor:
                          message.role === "agent" ? "#e6f7ff" : "#f5f5f5",
                        marginBottom: 8,
                      }}
                    >
                      <Space direction="vertical" size={4}>
                        <Text strong>
                          {message.role === "agent" ? "Agent" : "Client"}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {message.time_in_call_secs
                            ? new Date(message.time_in_call_secs * 1000)
                                .toISOString()
                                .substr(11, 8)
                            : ""}
                        </Text>
                        <Paragraph style={{ margin: 0 }}>
                          {message.message}
                        </Paragraph>
                      </Space>
                    </Card>
                  ),
                }))}
              />
            </div>
          ) : (
            <Empty description="No transcription available" />
          )}
        </Space>
      ),
    },
    // {
    //   key: "client-data",
    //   label: "Client Data",
    //   children: (
    //     <Space direction="vertical" size="large" style={{ width: "100%" }}>
    //       <Title level={4}>Client Information</Title>

    //       {loading ? (
    //         <Spin tip="Loading client data..." />
    //       ) : clientData ? (
    //         <Row gutter={[16, 16]}>
    //           <Col xs={24} md={12}>
    //             <Card title="Client Details" size="small">
    //               <Descriptions column={1} size="small">
    //                 <Descriptions.Item label="Name">
    //                   {clientData.name}
    //                 </Descriptions.Item>
    //                 <Descriptions.Item label="Email">
    //                   {clientData.email}
    //                 </Descriptions.Item>
    //                 <Descriptions.Item label="Phone">
    //                   {clientData.phone}
    //                 </Descriptions.Item>
    //                 <Descriptions.Item label="Company">
    //                   {clientData.company || "N/A"}
    //                 </Descriptions.Item>
    //               </Descriptions>
    //             </Card>
    //           </Col>
    //           <Col xs={24} md={12}>
    //             <Card title="Recent Activity" size="small">
    //               <Descriptions column={1} size="small">
    //                 <Descriptions.Item label="Last Contact">
    //                   {new Date(clientData.last_contact).toLocaleDateString()}
    //                 </Descriptions.Item>
    //                 <Descriptions.Item label="Status">
    //                   {clientData.status}
    //                 </Descriptions.Item>
    //                 <Descriptions.Item label="Notes">
    //                   {clientData.notes || "No notes"}
    //                 </Descriptions.Item>
    //               </Descriptions>
    //             </Card>
    //           </Col>
    //         </Row>
    //       ) : (
    //         <Empty description="No client data available" />
    //       )}
    //     </Space>
    //   ),
    // },
    {
      key: "phone-call",
      label: "Phone Call",
      children: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={4}>Phone Call Details</Title>

          {activeTab === "phone-call" && console.log("Data ->", conversation.metadata)}

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Call Data" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Start Time">
                    {conversation.start_time
                      ? dayjs(conversation.start_time).format("DD-MM-YYYY hh:mm:ss A")
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="End Time">
                    {conversation.end_time
                      ? dayjs(conversation.end_time).format("DD-MM-YYYY hh:mm:ss A")
                      : "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Duration">
                    {formatDuration(conversation.call_duration) || "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Call Participants" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Direction">
                    {conversation.metadata?.phone_call?.direction || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Caller Number">
                    {conversation.metadata?.phone_call?.agent_number || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Receiver Number">
                    {conversation.metadata?.phone_call?.external_number || "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Space>
      ),
    },
    {
      key: "batch-call",
      label: "Batch Call",
      children: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={4}>Batch Call Information</Title>
          {loading ? (
            <Spin tip="Loading batch data..." />
          ) : batchData ? (
            <Descriptions
              title="Batch Call Details"
              bordered
              size="small"
              column={1}
            >
              <Descriptions.Item label="Batch Name">
                {batchData.name}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={
                    batchData.status === "completed" ? "success" : "processing"
                  }
                  text={batchData.status}
                />
              </Descriptions.Item>
              {/* <Descriptions.Item label="Total Calls">{batchData.total_calls_dispatched || batchData.total_calls || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Completed">{batchData.completed_calls || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Failed">{batchData.failed_calls || "N/A"}</Descriptions.Item> */}
              <Descriptions.Item label="Created At">
                {batchData.created_at
                  ? new Date(batchData.created_at).toLocaleString()
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty description="No batch call data available" />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: "100%", margin: "0 auto" }}>
      {/* <div style={{ 
        backgroundColor: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6', 
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Title level={4} style={{ margin: 0 }}>Conversation Details</Title>
        <Button 
          type="text" 
          icon={<CloseOutlined />} 
          onClick={onClose}
          style={{ border: 'none' }}
        />
      </div> */}

      <div style={{ padding: "1rem" }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="middle"
        />
      </div>
    </div>
  );
};

export default ConversationDetailTabs;
